/**
 * Phase 24 / Task 24.9 (DEC-100) — in-app `notifications` (tenant-scoped, RLS).
 *
 * A minimal role-targeted notification stream (master spec §6 notification model
 * is broader; this is the slice Task 24.9 needs). On Paelode approval of an
 * OPERATOR_ID_CHANGED, the SECURITY DEFINER catalog_change_apply_approval fn
 * inserts one BILLING_MANAGER notification per affected tenant ("Well X changed
 * operators … review any open invoices referencing the old operator"). The
 * billing role reads its unread stream via GET /v1/notifications and marks read
 * via PATCH /v1/notifications/:id/read.
 *
 * user_role is the RoleCode the notification targets; GET filters on the caller's
 * own roleCode so a BILLING_MANAGER sees only BILLING_MANAGER notifications.
 */
import { boolean, index, pgPolicy, pgTable, text, timestamp, uuid } from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { createInsertSchema, createSelectSchema } from 'drizzle-zod';
import { tenants } from './users.js';
import { appUserRole } from './enums.js';

export const notifications = pgTable(
  'notifications',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    // The RoleCode this notification targets (e.g. 'BILLING_MANAGER').
    userRole: text('user_role').notNull(),
    message: text('message').notNull(),
    isRead: boolean('is_read').notNull().default(false),
    relatedEntityType: text('related_entity_type'),
    relatedEntityId: uuid('related_entity_id'),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    index('notifications_tenant_role_read_idx').on(t.tenantId, t.userRole, t.isRead),
    pgPolicy('notifications_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
      withCheck: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
    }),
  ],
);

export type Notification = typeof notifications.$inferSelect;
export type NewNotification = typeof notifications.$inferInsert;
export const notificationSelectSchema = createSelectSchema(notifications);
export const notificationInsertSchema = createInsertSchema(notifications);
